import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, RefreshCw, AlertTriangle, TrendingDown, Package, ArrowRight } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';

const PRIORITY_STYLES = {
  critical: 'bg-error/20 text-error border-error/30',
  high: 'bg-warning/20 text-warning border-warning/30',
  medium: 'bg-info/20 text-info border-info/30',
};

export default function ProcurementPage() {
  const [data, setData] = useState({ suggestions: [], summary: {} });
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/procurement/suggestions');
      setData(res.data || { suggestions: [], summary: {} });
    } catch { toast.error('Failed to load suggestions'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const s = data.summary || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="procurement-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-200/50">
              <ShoppingCart size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="procurement-title">Procurement Suggestions</h1>
              <p className="text-sm text-muted-foreground mt-0.5">AI-powered purchase order recommendations based on stock trends</p>
            </div>
          </div>
          <Button onClick={fetch} variant="outline" className="rounded-full h-9 text-xs" data-testid="refresh-btn">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card className="border-border"><CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground font-medium">Items to Order</p>
            <p className="text-2xl font-bold font-outfit">{s.total_items || 0}</p>
          </CardContent></Card>
          <Card className="border-error/30 bg-error/10"><CardContent className="p-3 text-center">
            <p className="text-[10px] text-error font-medium">Critical</p>
            <p className="text-2xl font-bold font-outfit text-error">{s.critical || 0}</p>
          </CardContent></Card>
          <Card className="border-warning/30 bg-warning/10"><CardContent className="p-3 text-center">
            <p className="text-[10px] text-warning font-medium">High</p>
            <p className="text-2xl font-bold font-outfit text-warning">{s.high || 0}</p>
          </CardContent></Card>
          <Card className="border-info/30 bg-info/10"><CardContent className="p-3 text-center">
            <p className="text-[10px] text-info font-medium">Medium</p>
            <p className="text-2xl font-bold font-outfit text-info">{s.medium || 0}</p>
          </CardContent></Card>
          <Card className="border-success/30 bg-success/10"><CardContent className="p-3 text-center">
            <p className="text-[10px] text-success font-medium">Est. Cost</p>
            <p className="text-xl font-bold font-outfit text-success flex items-center justify-center gap-1"><RiyalIcon size={14} />{(s.estimated_total_cost || 0).toLocaleString()}</p>
          </CardContent></Card>
        </div>

        {/* Suggestions List */}
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw size={24} className="animate-spin text-orange-500" /></div>
        ) : data.suggestions.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <Package size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">All stock levels are healthy. No procurement needed right now.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="suggestions-list">
            {data.suggestions.map((sg, i) => (
              <Card key={sg.item_id} className={`border-l-4 ${sg.priority === 'critical' ? 'border-l-red-500' : sg.priority === 'high' ? 'border-l-amber-500' : 'border-l-blue-500'}`} data-testid={`suggestion-${i}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm">{sg.item_name}</p>
                        <Badge className={`text-[10px] border ${PRIORITY_STYLES[sg.priority]}`}>{sg.priority}</Badge>
                        {sg.category && <Badge variant="outline" className="text-[9px]">{sg.category}</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>Balance: <strong className={sg.current_balance <= 0 ? 'text-error' : 'text-foreground'}>{sg.current_balance} {sg.unit}</strong></span>
                        <span>Min: <strong>{sg.min_stock_level} {sg.unit}</strong></span>
                        <span>Monthly Use: <strong>{sg.monthly_consumption} {sg.unit}</strong></span>
                        {sg.days_until_stockout && <span className="text-error">Stockout in <strong>{sg.days_until_stockout}d</strong></span>}
                        {sg.alert_frequency > 0 && <span>Alerts: <strong>{sg.alert_frequency}x</strong></span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-[10px] text-muted-foreground">Suggested Order</p>
                        <p className="text-lg font-bold text-orange-600">{sg.suggested_order_qty} {sg.unit}</p>
                        {sg.estimated_cost > 0 && (
                          <p className="text-xs text-muted-foreground flex items-center justify-end gap-0.5"><RiyalIcon size={10} />{sg.estimated_cost.toLocaleString()}</p>
                        )}
                      </div>
                      {sg.preferred_supplier_name && (
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground">Supplier</p>
                          <p className="text-xs font-medium">{sg.preferred_supplier_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
